import { API_BASE, api } from "@/lib/apiClient";

export type StuecklisteStatus = "draft" | "fertig";
export type StuecklisteSourceType = "hero" | "manual" | "order_line";
export type StuecklistePositionKind = "standard" | "werkzeug";
export type StuecklistePack = "montage" | "werkstatt";

export type StuecklistePositionStatus =
  | "offen"
  | "lager"
  | "bestellt"
  | "kommissioniert"
  | "nicht_vorhanden";

export type StuecklisteTkStatus = "offen" | "geliefert" | "storniert";
export type StuecklisteSchnittlage = "gegenlaeufig" | "gleichlaeufig";

export type BeschichtungArt = "roh" | "grundiert" | "verzinkt" | "gepulvert" | "lackiert";
export type BeschichtungGrundiertTon = "hell" | "dunkel";
export type BeschichtungFinish = "gepulvert" | "lackiert";

export const STUECKLISTE_SCHNITTLAGE_OPTIONS: {
  value: StuecklisteSchnittlage;
  label: string;
}[] = [
  { value: "gegenlaeufig", label: "Gegenläufig (/\\)" },
  { value: "gleichlaeufig", label: "Gleichläufig (//)" },
];

export const STUECKLISTE_POSITION_STATUS_OPTIONS: {
  value: StuecklistePositionStatus;
  label: string;
}[] = [
  { value: "offen", label: "Offen" },
  { value: "lager", label: "Im Lager vorhanden" },
  { value: "nicht_vorhanden", label: "Nicht vorhanden" },
  { value: "bestellt", label: "Bestellt" },
  { value: "kommissioniert", label: "Kommissioniert" },
];

export const STUECKLISTE_POSITION_STATUS_LABELS: Record<StuecklistePositionStatus, string> = {
  offen: "Offen",
  lager: "Im Lager vorhanden",
  nicht_vorhanden: "Nicht vorhanden",
  bestellt: "Bestellt",
  kommissioniert: "Kommissioniert",
};

export const BESCHICHTUNG_ART_OPTIONS: { value: BeschichtungArt; label: string }[] = [
  { value: "roh", label: "Roh" },
  { value: "grundiert", label: "Grundiert" },
  { value: "verzinkt", label: "Verzinkt" },
  { value: "gepulvert", label: "Gepulvert" },
  { value: "lackiert", label: "Lackiert" },
];

export interface StuecklistePosition {
  id?: number;
  position_nr: number;
  position_kind?: StuecklistePositionKind | string;
  anzahl: number | null;
  groesse: string | null;
  beschreibung: string | null;
  bemerkung: string | null;
  artikel_id: number | null;
  hero_nr: string | null;
  source_type: StuecklisteSourceType | string;
  position_status: StuecklistePositionStatus | string;
  order_line_id: number | null;
  bestellnummer: string | null;
  zuschnitt_erforderlich: boolean;
  ist_montagematerial: boolean;
  schnittwinkel_links?: number | null;
  schnittwinkel_rechts?: number | null;
  schnittlage?: StuecklisteSchnittlage | string | null;
  tk_status?: StuecklisteTkStatus | string | null;
  tk_status_label?: string | null;
}

export interface Stueckliste {
  id: number;
  auftrag_id: number;
  status: StuecklisteStatus | string;
  pack?: StuecklistePack | string;
  datum: string | null;
  bauteil: string | null;
  beschichtung: string | null;
  beschichtung_art: BeschichtungArt | string | null;
  beschichtung_grundiert_ton: BeschichtungGrundiertTon | string | null;
  beschichtung_finish: BeschichtungFinish | string | null;
  beschichtung_ral: string | null;
  beschichtung_display?: string | null;
  notizen?: string | null;
  dokument_nummer: string | null;
  display_number?: string | null;
  revision_index?: number;
  base_stueckliste_id?: number | null;
  supersedes_stueckliste_id?: number | null;
  file_name: string | null;
  sharepoint_item_id: string | null;
  sharepoint_web_url: string | null;
  montageunterlage_id: number | null;
  created_by_login: string;
  created_by_display: string | null;
  created_at: string;
  updated_at: string;
  finalized_at: string | null;
  positionen: StuecklistePosition[];
  auftrag_nummer?: string | null;
  auftrag_beschreibung?: string | null;
  kunde_name?: string | null;
  execution_class?: "EXC1" | "EXC2" | string | null;
  public_view_token?: string | null;
  public_view_url?: string | null;
}

export interface StuecklisteUpsertPayload {
  auftrag_id?: number;
  pack?: StuecklistePack | string;
  datum?: string | null;
  bauteil?: string | null;
  beschichtung_art?: BeschichtungArt | string | null;
  beschichtung_grundiert_ton?: BeschichtungGrundiertTon | string | null;
  beschichtung_finish?: BeschichtungFinish | string | null;
  beschichtung_ral?: string | null;
  notizen?: string | null;
  positionen: StuecklistePosition[];
}

export function isWerkstattStueckliste(item: Pick<Stueckliste, "pack">): boolean {
  return String(item.pack || "montage").toLowerCase() === "werkstatt";
}

export interface StuecklisteWerkzeugVorschlag {
  beschreibung: string;
  usage_count: number;
}

export interface StuecklisteTkVorschlag {
  order_line_id: number;
  order_id: number;
  bestellnummer: string | null;
  text: string | null;
  menge: number | null;
  einheit: string | null;
  hero_nr: string | null;
  order_cancelled: boolean;
  tk_status: StuecklisteTkStatus | string;
  tk_status_label: string;
  already_linked: boolean;
}

export interface StuecklisteTkNummer {
  bestellnummer: string;
  order_id: number | null;
  tk_status: StuecklisteTkStatus | string;
  tk_status_label: string;
  position_count: number;
}

export function normalizePosition(p: StuecklistePosition): StuecklistePosition {
  const kind = String(p.position_kind || "standard").toLowerCase() === "werkzeug"
    ? "werkzeug"
    : "standard";
  if (kind === "werkzeug") {
    return {
      id: p.id,
      position_nr: p.position_nr || 1,
      position_kind: "werkzeug",
      anzahl: p.anzahl ?? null,
      groesse: p.groesse || null,
      beschreibung: p.beschreibung || null,
      bemerkung: p.bemerkung || null,
      artikel_id: null,
      hero_nr: null,
      source_type: "manual",
      position_status: "offen",
      order_line_id: null,
      bestellnummer: null,
      zuschnitt_erforderlich: false,
      ist_montagematerial: false,
      schnittwinkel_links: null,
      schnittwinkel_rechts: null,
      schnittlage: null,
    };
  }
  const status = (p.position_status || "offen") as StuecklistePositionStatus;
  const isBestellt = status === "bestellt";
  const zuschnitt = Boolean(p.zuschnitt_erforderlich);
  const left = zuschnitt ? (p.schnittwinkel_links ?? 90) : null;
  const right = zuschnitt ? (p.schnittwinkel_rechts ?? 90) : null;
  const needsLage = zuschnitt && ((left ?? 90) !== 90 || (right ?? 90) !== 90);
  return {
    id: p.id,
    position_nr: p.position_nr || 1,
    position_kind: "standard",
    anzahl: p.anzahl ?? null,
    groesse: p.groesse || null,
    beschreibung: p.beschreibung || null,
    bemerkung: p.bemerkung || null,
    artikel_id: p.artikel_id ?? null,
    hero_nr: p.hero_nr || null,
    source_type: p.source_type || "manual",
    position_status: status,
    order_line_id: isBestellt ? (p.order_line_id ?? null) : null,
    bestellnummer: isBestellt ? (p.bestellnummer || null) : null,
    zuschnitt_erforderlich: zuschnitt,
    ist_montagematerial: Boolean(p.ist_montagematerial),
    schnittwinkel_links: left,
    schnittwinkel_rechts: right,
    schnittlage: needsLage
      ? ((p.schnittlage as StuecklisteSchnittlage) || "gegenlaeufig")
      : null,
  };
}

export async function listAuftragStuecklisten(auftragId: number): Promise<Stueckliste[]> {
  return api.get<Stueckliste[]>(`/auftraege/${encodeURIComponent(String(auftragId))}/stuecklisten`);
}

export async function listStuecklisteTkVorschlaege(auftragId: number): Promise<StuecklisteTkVorschlag[]> {
  return api.get<StuecklisteTkVorschlag[]>(
    `/auftraege/${encodeURIComponent(String(auftragId))}/stuecklisten/tk-vorschlaege`,
  );
}

export async function listStuecklisteTkNummern(auftragId: number): Promise<StuecklisteTkNummer[]> {
  return api.get<StuecklisteTkNummer[]>(
    `/auftraege/${encodeURIComponent(String(auftragId))}/stuecklisten/tk-nummern`,
  );
}

export async function getStueckliste(id: number): Promise<Stueckliste> {
  return api.get<Stueckliste>(`/stuecklisten/${encodeURIComponent(String(id))}`);
}

export async function createStueckliste(
  auftragId: number,
  payload: StuecklisteUpsertPayload,
): Promise<Stueckliste> {
  return api.post<Stueckliste>("/stuecklisten", {
    auftrag_id: auftragId,
    pack: payload.pack ?? "montage",
    datum: payload.datum ?? null,
    bauteil: payload.bauteil ?? null,
    beschichtung_art: payload.beschichtung_art ?? null,
    beschichtung_grundiert_ton: payload.beschichtung_grundiert_ton ?? null,
    beschichtung_finish: payload.beschichtung_finish ?? null,
    beschichtung_ral: payload.beschichtung_ral ?? null,
    notizen: payload.notizen ?? null,
    positionen: (payload.positionen || []).map(normalizePosition),
  });
}

export async function updateStueckliste(
  id: number,
  payload: StuecklisteUpsertPayload,
): Promise<Stueckliste> {
  return api.put<Stueckliste>(`/stuecklisten/${encodeURIComponent(String(id))}`, {
    pack: payload.pack,
    datum: payload.datum ?? null,
    bauteil: payload.bauteil ?? null,
    beschichtung_art: payload.beschichtung_art ?? null,
    beschichtung_grundiert_ton: payload.beschichtung_grundiert_ton ?? null,
    beschichtung_finish: payload.beschichtung_finish ?? null,
    beschichtung_ral: payload.beschichtung_ral ?? null,
    notizen: payload.notizen ?? null,
    positionen: (payload.positionen || []).map(normalizePosition),
  });
}

export async function listStuecklisteWerkzeugVorschlaege(
  q = "",
): Promise<StuecklisteWerkzeugVorschlag[]> {
  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  const qs = params.toString();
  return api.get<StuecklisteWerkzeugVorschlag[]>(
    `/stuecklisten/werkzeug-vorschlaege${qs ? `?${qs}` : ""}`,
  );
}

export async function deleteStueckliste(id: number): Promise<void> {
  await api.del(`/stuecklisten/${encodeURIComponent(String(id))}`);
}

export async function finalizeStueckliste(id: number): Promise<Stueckliste> {
  return api.post<Stueckliste>(`/stuecklisten/${encodeURIComponent(String(id))}/finalize`);
}

export async function setStuecklistePack(
  id: number,
  pack: StuecklistePack | string,
): Promise<Stueckliste> {
  const params = new URLSearchParams({ pack: String(pack || "montage") });
  return api.post<Stueckliste>(
    `/stuecklisten/${encodeURIComponent(String(id))}/set-pack?${params.toString()}`,
  );
}

export function normalizeStuecklistePack(
  value: StuecklistePack | string | null | undefined,
): StuecklistePack {
  return String(value || "montage").toLowerCase() === "werkstatt" ? "werkstatt" : "montage";
}

export async function createStuecklisteRevision(id: number): Promise<Stueckliste> {
  return api.post<Stueckliste>(
    `/stuecklisten/${encodeURIComponent(String(id))}/create-revision`,
  );
}

export interface StuecklisteSearchResult {
  id: number;
  auftrag_id: number;
  status: StuecklisteStatus | string;
  pack?: StuecklistePack | string;
  bauteil: string | null;
  display_number?: string | null;
  dokument_nummer: string | null;
  revision_index?: number;
  auftrag_nummer?: string | null;
  kunde_name?: string | null;
  position_count: number;
  created_at: string;
}

export interface StuecklisteClonePayload {
  target_auftrag_id: number;
  keep_order_links?: boolean;
  pack?: StuecklistePack | string | null;
}

export async function searchStuecklisten(
  q: string,
  limit = 20,
): Promise<StuecklisteSearchResult[]> {
  const params = new URLSearchParams();
  params.set("q", q.trim());
  if (limit !== 20) params.set("limit", String(limit));
  return api.get<StuecklisteSearchResult[]>(`/stuecklisten/search?${params.toString()}`);
}

export async function cloneStueckliste(
  id: number,
  payload: StuecklisteClonePayload,
): Promise<Stueckliste> {
  return api.post<Stueckliste>(`/stuecklisten/${encodeURIComponent(String(id))}/clone`, {
    target_auftrag_id: payload.target_auftrag_id,
    keep_order_links: Boolean(payload.keep_order_links),
    pack: payload.pack ?? null,
  });
}

export function stuecklisteDisplayNumber(sl: Pick<Stueckliste, "display_number" | "dokument_nummer" | "revision_index" | "id">): string {
  const display = String(sl.display_number || "").trim();
  if (display) return display;
  const base = String(sl.dokument_nummer || "").trim();
  const revision = Number(sl.revision_index || 0);
  if (base && revision > 0) return `${base}.${revision}`;
  if (base) return base;
  return `Stückliste #${sl.id}`;
}

/** Pro Basis nur die aktuellste Revision (Entwurf bevorzugt, sonst höchster revision_index). */
export function pickLatestStuecklisten(items: Stueckliste[]): Stueckliste[] {
  const byBase = new Map<number, Stueckliste[]>();
  for (const item of items) {
    const baseId = item.base_stueckliste_id ?? item.id;
    const list = byBase.get(baseId) || [];
    list.push(item);
    byBase.set(baseId, list);
  }
  const latest: Stueckliste[] = [];
  for (const group of byBase.values()) {
    group.sort((a, b) => {
      const aDraft = String(a.status || "").toLowerCase() === "draft" ? 1 : 0;
      const bDraft = String(b.status || "").toLowerCase() === "draft" ? 1 : 0;
      if (aDraft !== bDraft) return bDraft - aDraft;
      const aRev = Number(a.revision_index || 0);
      const bRev = Number(b.revision_index || 0);
      if (aRev !== bRev) return bRev - aRev;
      return b.id - a.id;
    });
    latest.push(group[0]!);
  }
  return latest.sort((a, b) => {
    const at = a.created_at || "";
    const bt = b.created_at || "";
    return bt.localeCompare(at);
  });
}

export type ZuschnittBadgeParts = {
  angles: string;
  lageSymbol: string | null;
};

export function getZuschnittBadgeParts(pos: Pick<
  StuecklistePosition,
  "zuschnitt_erforderlich" | "schnittwinkel_links" | "schnittwinkel_rechts" | "schnittlage"
>): ZuschnittBadgeParts | null {
  if (!pos.zuschnitt_erforderlich) return null;
  const left = pos.schnittwinkel_links ?? 90;
  const right = pos.schnittwinkel_rechts ?? 90;
  const fmt = (v: number) => `${v}°`;
  const angles = `${fmt(left)} / ${fmt(right)}`;
  let lageSymbol: string | null = null;
  if (left !== 90 || right !== 90) {
    const lage = String(pos.schnittlage || "gegenlaeufig");
    lageSymbol = lage === "gleichlaeufig" ? "//" : "/\\";
  }
  return { angles, lageSymbol };
}

export function formatZuschnittLabel(pos: Pick<
  StuecklistePosition,
  "zuschnitt_erforderlich" | "schnittwinkel_links" | "schnittwinkel_rechts" | "schnittlage"
>): string | null {
  const parts = getZuschnittBadgeParts(pos);
  if (!parts) return null;
  return parts.lageSymbol ? `${parts.angles} · ${parts.lageSymbol}` : parts.angles;
}

export function stuecklistePdfUrl(id: number): string {
  return `${API_BASE}/stuecklisten/${encodeURIComponent(String(id))}/pdf`;
}

export async function fetchStuecklistePdf(id: number): Promise<Blob> {
  const res = await fetch(stuecklistePdfUrl(id), { credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export function emptyStuecklistePosition(positionNr = 1): StuecklistePosition {
  return {
    position_nr: positionNr,
    position_kind: "standard",
    anzahl: 1,
    groesse: null,
    beschreibung: null,
    bemerkung: null,
    artikel_id: null,
    hero_nr: null,
    source_type: "manual",
    position_status: "offen",
    order_line_id: null,
    bestellnummer: null,
    zuschnitt_erforderlich: false,
    ist_montagematerial: false,
    schnittwinkel_links: null,
    schnittwinkel_rechts: null,
    schnittlage: null,
  };
}

export function emptyStuecklisteWerkzeugPosition(positionNr = 1): StuecklistePosition {
  return {
    position_nr: positionNr,
    position_kind: "werkzeug",
    anzahl: 1,
    groesse: null,
    beschreibung: null,
    bemerkung: null,
    artikel_id: null,
    hero_nr: null,
    source_type: "manual",
    position_status: "offen",
    order_line_id: null,
    bestellnummer: null,
    zuschnitt_erforderlich: false,
    ist_montagematerial: false,
    schnittwinkel_links: null,
    schnittwinkel_rechts: null,
    schnittlage: null,
  };
}

export function isWerkzeugPosition(pos: Pick<StuecklistePosition, "position_kind">): boolean {
  return String(pos.position_kind || "standard").toLowerCase() === "werkzeug";
}

/** Verschiebt eine Position; `to` ist der Einfügeindex vor dem Entfernen (wie Drop-Marker). */
export function reorderStuecklistePositions(
  positions: StuecklistePosition[],
  from: number,
  to: number,
): StuecklistePosition[] {
  if (from === to || from < 0 || from >= positions.length) return positions;
  const next = [...positions];
  const [moved] = next.splice(from, 1);
  if (!moved) return positions;
  let insertAt = to;
  if (from < to) insertAt -= 1;
  insertAt = Math.max(0, Math.min(insertAt, next.length));
  next.splice(insertAt, 0, moved);
  return next.map((row, idx) => ({ ...row, position_nr: idx + 1 }));
}

/** Dupliziert eine Position und fügt die Kopie direkt danach ein.
 *  TK-/Bestellverknüpfung bleibt erhalten (eine Bestellzeile → viele Zuschnitte). */
export function duplicateStuecklistePositionInList(
  positions: StuecklistePosition[],
  index: number,
): StuecklistePosition[] {
  const source = positions[index];
  if (!source) return positions;
  const { id: _id, ...rest } = source;
  const copy: StuecklistePosition = { ...rest };
  const insertAt = index + 1;
  const next = [...positions.slice(0, insertAt), copy, ...positions.slice(insertAt)];
  return next.map((row, idx) => ({ ...row, position_nr: idx + 1 }));
}

export function tkVorschlagToPosition(
  vorschlag: StuecklisteTkVorschlag,
  positionNr: number,
): StuecklistePosition {
  return {
    position_nr: positionNr,
    position_kind: "standard",
    anzahl: vorschlag.menge ?? 1,
    groesse: null,
    beschreibung: vorschlag.text || null,
    bemerkung: null,
    artikel_id: null,
    hero_nr: vorschlag.hero_nr || null,
    source_type: "order_line",
    position_status: "bestellt",
    order_line_id: vorschlag.order_line_id,
    bestellnummer: vorschlag.bestellnummer || null,
    zuschnitt_erforderlich: false,
    ist_montagematerial: false,
    schnittwinkel_links: null,
    schnittwinkel_rechts: null,
    schnittlage: null,
    tk_status: vorschlag.tk_status,
    tk_status_label: vorschlag.tk_status_label,
  };
}

export function positionStatusLabel(status: string | null | undefined): string {
  const key = String(status || "offen").toLowerCase() as StuecklistePositionStatus;
  return STUECKLISTE_POSITION_STATUS_LABELS[key] || String(status || "Offen");
}

const BESCHICHTUNG_FINISH_LABELS: Record<BeschichtungFinish, string> = {
  gepulvert: "Gepulvert",
  lackiert: "Lackiert",
};

export function formatBeschichtungDisplay(input: {
  beschichtung_art?: string | null;
  beschichtung_grundiert_ton?: string | null;
  beschichtung_finish?: string | null;
  beschichtung_ral?: string | null;
  beschichtung_display?: string | null;
  beschichtung?: string | null;
}): string {
  if (input.beschichtung_display) return input.beschichtung_display;
  const art = String(input.beschichtung_art || "").trim().toLowerCase();
  if (!art) return String(input.beschichtung || "").trim();
  const label = BESCHICHTUNG_ART_OPTIONS.find((o) => o.value === art)?.label || art;
  if (art === "grundiert" && input.beschichtung_grundiert_ton) {
    return `${label} (${input.beschichtung_grundiert_ton})`;
  }
  if (art === "verzinkt") {
    const finish = String(input.beschichtung_finish || "").trim().toLowerCase() as BeschichtungFinish;
    if (finish && input.beschichtung_ral) {
      const finishLabel = BESCHICHTUNG_FINISH_LABELS[finish] || finish;
      return `${label}, ${finishLabel}, RAL ${input.beschichtung_ral}`;
    }
    return label;
  }
  if ((art === "gepulvert" || art === "lackiert") && input.beschichtung_ral) {
    return `${label}, RAL ${input.beschichtung_ral}`;
  }
  return label;
}
